import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, FileText } from 'lucide-react';
import { useI18n } from '@/lib/i18n';
import { useWorkspace } from '@/hooks/useWorkspace';
import { useAuth } from '@/hooks/useAuth';
import { Button } from '@/components/ui/button';
import { showToast } from '@/components/ui/toaster';
import { payrollService } from '@/services/payroll.service';
import { formatRp } from '@/lib/currency';

export default function PayslipDetailPage() {
    const { t } = useI18n();
    const { id } = useParams<{ id: string }>();
    const { user } = useAuth();
    const { currentWorkspace } = useWorkspace();
    const navigate = useNavigate();
    const [slip, setSlip] = useState<any | null>(null);
    const [profile, setProfile] = useState<any | null>(null);

    const otBreakdown = getOvertimeBreakdown(slip);
    const unpaidLeaveDeduction = getUnpaidLeaveDeduction(slip);
    const unpaidLeaveDays = getUnpaidLeaveDays(slip);

    useEffect(() => {
        if (!id) return;
        (async () => {
            try {
                const res = await payrollService.getPayslip(id);
                setSlip(res.data);
                if (currentWorkspace) {
                    try {
                        const meRes = await payrollService.getMyProfile(currentWorkspace._id);
                        if (meRes.data) setProfile(meRes.data);
                    } catch {}
                }
            } catch (e: any) {
                showToast({ variant: 'destructive', description: e?.response?.data?.error || 'Gagal memuat' });
            }
        })();
    }, [id, currentWorkspace?._id]);

    const downloadPdf = async () => {
        if (!slip) return;
        try {
            const { jsPDF } = await import('jspdf');
            const doc = new jsPDF();
            const employeeName = user?.displayName || user?.name || 'Karyawan';
            const wsName = currentWorkspace?.name || 'Workspace';

            // Header
            doc.setFillColor(124, 58, 237);
            doc.rect(0, 0, 210, 28, 'F');
            doc.setTextColor(255, 255, 255);
            doc.setFontSize(18);
            doc.setFont('helvetica', 'bold');
            doc.text(wsName, 14, 14);
            doc.setFontSize(10);
            doc.setFont('helvetica', 'normal');
            doc.text('Slip Gaji', 14, 22);
            doc.text(`Periode ${slip.period}`, 196, 22, { align: 'right' });

            // Meta
            doc.setTextColor(0, 0, 0);
            doc.setFontSize(10);
            let y = 40;
            const meta: [string, string][] = [
                ['Nama Karyawan', employeeName],
                ['Status PTKP', profile?.ptkpStatus || '-'],
                ['NPWP', profile?.npwp || '-'],
                ['Jenis Karyawan', profile?.employeeType || '-'],
            ];
            for (const [label, value] of meta) {
                doc.setTextColor(120, 120, 120);
                doc.text(label, 14, y);
                doc.setTextColor(0, 0, 0);
                doc.text(value, 60, y);
                y += 6;
            }
            y += 4;

            // Pendapatan
            doc.setFillColor(243, 232, 255);
            doc.rect(14, y - 4, 182, 7, 'F');
            doc.setFont('helvetica', 'bold');
            doc.text('PENDAPATAN', 16, y);
            doc.setFont('helvetica', 'normal');
            y += 8;
            const earnings: [string, number][] = [['Gaji Pokok', slip.basicSalary]];
            for (const c of slip.components) {
                if (c.type === 'add') earnings.push([c.name, c.amount]);
            }
            if (slip.overtimePay > 0) earnings.push(['Lembur', slip.overtimePay]);
            if (slip.reimbursementPay > 0) earnings.push(['Reimbursement', slip.reimbursementPay]);
            let totalEarnings = 0;
            for (const [label, amt] of earnings) {
                doc.text(label, 16, y);
                doc.text(formatRp(amt), 194, y, { align: 'right' });
                totalEarnings += amt;
                y += 6;
                // Rincian tier lembur (indent di bawah baris Lembur)
                if (label === 'Lembur' && otBreakdown.length > 0) {
                    doc.setFontSize(8);
                    doc.setTextColor(130, 130, 130);
                    for (const b of otBreakdown) {
                        const tier = otTierLabel(t, b.label);
                        const mult = b.multiplier ? ` (x${b.multiplier})` : '';
                        doc.text(`  ${tier} · ${b.minutes} mnt${mult}`, 18, y);
                        doc.text(formatRp(b.amount), 194, y, { align: 'right' });
                        y += 5;
                    }
                    doc.setFontSize(10);
                    doc.setTextColor(0, 0, 0);
                }
            }
            doc.setLineWidth(0.2); doc.line(14, y, 196, y); y += 6;
            doc.setFont('helvetica', 'bold');
            doc.text('Total Pendapatan', 16, y);
            doc.text(formatRp(totalEarnings), 194, y, { align: 'right' });
            doc.setFont('helvetica', 'normal');
            y += 10;

            // Potongan
            doc.setFillColor(254, 226, 226);
            doc.rect(14, y - 4, 182, 7, 'F');
            doc.setFont('helvetica', 'bold');
            doc.text('POTONGAN', 16, y);
            doc.setFont('helvetica', 'normal');
            y += 8;
            const deductions: [string, number][] = [];
            for (const c of slip.components) {
                if (c.type === 'sub') deductions.push([c.name, c.amount]);
            }
            if (unpaidLeaveDeduction > 0) {
                const daysLabel = unpaidLeaveDays > 0 ? ` (${unpaidLeaveDays} hari)` : '';
                deductions.push([`Izin Tanpa Bayar${daysLabel}`, unpaidLeaveDeduction]);
            }
            if (slip.latenessDeduction > 0) deductions.push(['Potongan Keterlambatan', slip.latenessDeduction]);
            if (slip.pph21 > 0) deductions.push(['PPh21', slip.pph21]);
            let totalDeductions = 0;
            for (const [label, amt] of deductions) {
                doc.text(label, 16, y);
                doc.text(`-${formatRp(amt)}`, 194, y, { align: 'right' });
                totalDeductions += amt;
                y += 6;
            }
            if (deductions.length === 0) {
                doc.setTextColor(150, 150, 150);
                doc.text('Tidak ada potongan', 16, y);
                doc.setTextColor(0, 0, 0);
                y += 6;
            }
            doc.line(14, y, 196, y); y += 6;
            doc.setFont('helvetica', 'bold');
            doc.text('Total Potongan', 16, y);
            doc.text(`-${formatRp(totalDeductions)}`, 194, y, { align: 'right' });
            doc.setFont('helvetica', 'normal');
            y += 14;

            // Netto
            doc.setFillColor(232, 243, 232);
            doc.rect(14, y - 6, 182, 14, 'F');
            doc.setFont('helvetica', 'bold');
            doc.setFontSize(13);
            doc.text('GAJI BERSIH', 16, y + 2);
            doc.text(formatRp(slip.netSalary), 194, y + 2, { align: 'right' });
            doc.setFontSize(10);
            doc.setFont('helvetica', 'normal');
            y += 22;

            if (profile?.bankCode && profile?.bankAccountNo) {
                doc.setTextColor(120, 120, 120);
                doc.text('Ditransfer ke', 14, y);
                doc.setTextColor(0, 0, 0);
                doc.text(`${profile.bankCode.toUpperCase()} ${profile.bankAccountNo} a/n ${profile.bankAccountName || employeeName}`, 60, y);
                y += 8;
            }

            doc.setFontSize(8);
            doc.setTextColor(150, 150, 150);
            doc.text('Slip ini di-generate otomatis dan sah tanpa tanda tangan basah.', 14, 285);
            doc.text(`Dicetak ${new Date().toLocaleString('id-ID')}`, 196, 285, { align: 'right' });

            doc.save(`slip-${slip.period}-${employeeName.replace(/\s+/g, '_')}.pdf`);
        } catch (e: any) {
            showToast({ variant: 'destructive', description: 'Gagal generate PDF' });
        }
    };

    if (!slip) {
        return <div className="px-4 pt-12 max-w-md mx-auto text-center text-sm text-gray-400">{t('common.loading')}</div>;
    }

    return (
        <div className="px-4 pt-3 max-w-md mx-auto">
            <button onClick={() => navigate(-1)} className="flex items-center gap-1 text-sm text-gray-500 mb-3">
                <ArrowLeft className="h-4 w-4" /> {t('common.back')}
            </button>

            <div className="bg-white rounded-2xl p-5 space-y-3" style={{ border: '1px solid #e0d6cc' }}>
                <div>
                    <h1 className="text-base font-bold">{t('payslip.title')}</h1>
                    <p className="text-xs text-gray-500">{t('payslip.period')} {slip.period}</p>
                </div>

                <div className="space-y-1.5 text-sm pt-2" style={{ borderTop: '1px solid #f0ebe5' }}>
                    <Row label={t('payslip.basic_salary')} value={formatRp(slip.basicSalary)} />
                    {slip.components.map((c: any, i: number) => (
                        <Row key={i} label={`${c.name} (${c.type === 'add' ? '+' : '−'})`} value={formatRp(c.amount)} />
                    ))}
                    <Row label={t('payslip.overtime_pay')} value={formatRp(slip.overtimePay)} />
                    {otBreakdown.length > 0 && (
                        <div className="pl-3 pt-0.5 pb-1 space-y-1" style={{ borderLeft: '2px solid #f0ebe5' }}>
                            <div className="text-[11px] font-semibold text-gray-400 uppercase tracking-wide">
                                {t('payslip.overtime_breakdown')}
                            </div>
                            {otBreakdown.map((b, i) => (
                                <div key={i} className="flex justify-between text-xs text-gray-500">
                                    <span>
                                        {otTierLabel(t, b.label)}
                                        <span className="text-gray-400">
                                            {' · '}
                                            {t('payslip.ot_minutes', { m: b.minutes })}
                                            {b.multiplier ? ` · ${t('payslip.ot_multiplier', { x: b.multiplier })}` : ''}
                                        </span>
                                    </span>
                                    <span className="font-medium text-gray-700">{formatRp(b.amount)}</span>
                                </div>
                            ))}
                        </div>
                    )}
                    <Row label={t('payslip.reimbursement')} value={formatRp(slip.reimbursementPay)} />
                    {unpaidLeaveDeduction > 0 && (
                        <Row
                            label={`${t('payslip.unpaid_leave_deduction')}${unpaidLeaveDays > 0 ? ` (${t('payslip.unpaid_leave_days', { d: unpaidLeaveDays })})` : ''}`}
                            value={`-${formatRp(unpaidLeaveDeduction)}`}
                            negative
                        />
                    )}
                    <Row label={t('payslip.lateness_deduction')} value={`-${formatRp(slip.latenessDeduction)}`} negative />
                    <Row label={t('payslip.pph21')} value={`-${formatRp(slip.pph21)}`} negative />
                </div>

                <div className="pt-3" style={{ borderTop: '1px solid #f0ebe5' }}>
                    <Row label={t('payslip.gross')} value={formatRp(slip.grossSalary)} />
                    <Row label={t('payslip.net')} value={formatRp(slip.netSalary)} bold />
                </div>

                <Button onClick={downloadPdf} className="w-full" variant="outline">
                    <FileText className="h-4 w-4 mr-1" /> {t('payslip.download_pdf')}
                </Button>
            </div>
        </div>
    );
}

// ── OT breakdown helpers ──
// Backend stores computeOvertimePay() output: breakdown[] of { label, minutes, multiplier, amount }.
// Field name may vary across payroll versions, so read defensively from the slip + any JSON metadata.
interface OtBreakdownRow { label: string; minutes: number; multiplier?: number; amount: number; }

function parseMaybeJson(v: any): any {
    if (!v) return null;
    if (typeof v === 'object') return v;
    if (typeof v === 'string') { try { return JSON.parse(v); } catch { return null; } }
    return null;
}

function getOvertimeBreakdown(slip: any): OtBreakdownRow[] {
    if (!slip) return [];
    const meta = slip.meta || parseMaybeJson(slip.data) || parseMaybeJson(slip.notes) || {};
    const raw =
        slip.overtimeBreakdown ?? slip.otBreakdown ?? slip.overtime_breakdown ??
        meta.overtimeBreakdown ?? meta.otBreakdown ?? meta.overtime_breakdown;
    const arr = parseMaybeJson(raw) ?? raw;
    if (!Array.isArray(arr)) return [];
    return arr
        .map((b: any) => ({
            label: String(b?.label ?? ''),
            minutes: Number(b?.minutes ?? 0),
            multiplier: b?.multiplier != null ? Number(b.multiplier) : undefined,
            amount: Number(b?.amount ?? 0),
        }))
        .filter((b) => b.minutes > 0 || b.amount !== 0);
}

function getUnpaidLeaveDeduction(slip: any): number {
    if (!slip) return 0;
    const meta = slip.meta || parseMaybeJson(slip.data) || parseMaybeJson(slip.notes) || {};
    return Number(
        meta.unpaidLeave?.amount ??
        slip.unpaidLeaveDeduction ?? slip.unpaid_leave_deduction ??
        meta.unpaidLeaveDeduction ?? meta.unpaid_leave_deduction ?? 0,
    ) || 0;
}

function getUnpaidLeaveDays(slip: any): number {
    if (!slip) return 0;
    const meta = slip.meta || parseMaybeJson(slip.data) || parseMaybeJson(slip.notes) || {};
    return Number(
        meta.unpaidLeave?.days ??
        slip.unpaidLeaveDays ?? slip.unpaid_leave_days ??
        meta.unpaidLeaveDays ?? meta.unpaid_leave_days ?? 0,
    ) || 0;
}

// Map backend tier label -> localized string; fall back to the raw label.
function otTierLabel(t: (k: string, p?: Record<string, string | number>) => string, label: string): string {
    const key = label.toLowerCase().replace(/[\s-]+/g, '_');
    const map: Record<string, string> = {
        weekday_first: 'payslip.ot_weekday_first',
        first: 'payslip.ot_weekday_first',
        first_hour: 'payslip.ot_weekday_first',
        weekday_after: 'payslip.ot_weekday_after',
        after: 'payslip.ot_weekday_after',
        after_hour: 'payslip.ot_weekday_after',
        subsequent: 'payslip.ot_weekday_after',
        weekend: 'payslip.ot_weekend',
        rest_day: 'payslip.ot_weekend',
        holiday: 'payslip.ot_holiday',
        public_holiday: 'payslip.ot_holiday',
        night: 'payslip.ot_night',
        night_premium: 'payslip.ot_night',
        capped: 'payslip.ot_capped',
        cap: 'payslip.ot_capped',
    };
    const i18nKey = map[key];
    return i18nKey ? t(i18nKey) : label;
}

function Row({ label, value, bold, negative }: { label: string; value: string; bold?: boolean; negative?: boolean }) {
    return (
        <div className={`flex justify-between ${bold ? 'text-base font-black' : ''}`}>
            <span className={bold ? 'text-gray-900' : 'text-gray-500'}>{label}</span>
            <span className={`font-bold ${negative ? 'text-red-600' : 'text-gray-900'}`}>{value}</span>
        </div>
    );
}
